import React from "react";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import { Row, Col, Label, Button, Input } from "reactstrap";

type OptionType = { label: string; value: string };

interface Props {
	startDate: Date | null;
	endDate: Date | null;
	selectedUser: string;
	selectedRole: string;
	hasLogsOnly: boolean;

	userOptions: OptionType[];
	roleOptions: OptionType[];

	onStartDateChange: (date: Date) => void;
	onEndDateChange: (date: Date) => void;
	onUserChange: (val: string) => void;
	onRoleChange: (val: string) => void;
	onReset: () => void;
	onToggleHasLogsOnly: (val: boolean) => void;
}

const AuditSummaryFilters: React.FC<Props> = ({
	startDate,
	endDate,
	selectedUser,
	selectedRole,
	userOptions,
	roleOptions,
	hasLogsOnly,
	onStartDateChange,
	onEndDateChange,
	onUserChange,
	onRoleChange,
	onReset,
	onToggleHasLogsOnly
}) => {
	const toOption = (value: string, options: OptionType[]) =>
		options.find((opt) => opt.value === value) || null;

	return (
		<div className="bg-light p-3 rounded shadow-sm mb-3">
			{/* Title + Reset */}
			<div className="d-flex justify-content-start align-items-center mb-3 gap-2">
				<h5 className="fw-semibold mb-0">
					<i className="ri-filter-3-line me-2 text-primary" />
					Filters
				</h5>
				<Button
					color="danger"
					size="sm"
					className="btn-label"
					onClick={onReset}
					disabled={!startDate && !endDate && !selectedUser && !selectedRole}
				>
					<i className="ri-refresh-line label-icon align-middle fs-16 me-1"></i>
					Reset
				</Button>
			</div>

			{/* Filter Fields */}
			<Row className="g-3">
				{/* Start Date */}
				<Col md="3">
					<Label className="form-label fw-medium small mb-1">
						<i className="ri-calendar-event-line me-1 text-primary" />
						Date Range
					</Label>
					<div className="text-muted small mb-1">Pick a date range to filter logs</div>
					<Flatpickr
						className="form-control"
						options={{ mode: "range", dateFormat: "d M Y" }}
						value={[startDate, endDate].filter(Boolean) as Date[]}
						onChange={(dates) => {
							onStartDateChange(dates[0]);
							onEndDateChange(dates[1] || null);
						}}
						placeholder="Select date range"
					/>
				</Col>

				{/* User */}
				<Col md="3">
					<Label className="form-label fw-medium small mb-1">
						<i className="ri-user-line me-1 text-info" />
						User
					</Label>
					<div className="text-muted small mb-1">Filter by staff member</div>
					<Select
						classNamePrefix="react-select"
						options={[{ label: "All", value: "" }, ...userOptions]}
						value={toOption(selectedUser, userOptions) || { label: "All", value: "" }}
						onChange={(opt: any) => onUserChange(opt?.value || "")}
						isClearable
					/>
				</Col>

				{/* Role */}
				<Col md="3">
					<Label className="form-label fw-medium small mb-1">
						<i className="ri-shield-user-line me-1 text-secondary" />
						Role
					</Label>
					<div className="text-muted small mb-1">Filter by user role</div>
					<Select
						classNamePrefix="react-select"
						options={[{ label: "All", value: "" }, ...roleOptions]}
						value={toOption(selectedRole, roleOptions) || { label: "All", value: "" }}
						onChange={(opt: any) => onRoleChange(opt?.value || "")}
						isClearable
					/>
				</Col>

				<Col md="3">
					<Label className="form-label fw-medium small mb-1 d-flex align-items-center">
						<i className="ri-eye-line me-1 text-primary" />
						Show Only Users with Logs
					</Label>
					<div className="text-muted small mb-1">Exclude users without any footprint entries</div>
					<div className="form-check form-switch form-switch-md">
						<Input
							type="checkbox"
							className="form-check-input"
							id="hasLogsOnlySwitch"
							checked={hasLogsOnly}
							onChange={(e) => onToggleHasLogsOnly?.(e.target.checked)}
						/>
						<Label className="form-check-label ms-2 text-muted" htmlFor="hasLogsOnlySwitch">
							Enable
						</Label>
					</div>
				</Col>

			</Row>
		</div>
	);
};

export default AuditSummaryFilters;
